// Path: ./src/app/pages/CreateFlyerItemListPage/CreateFlyerItemListModal/index.tsx
import { useTranslation } from "react-i18next";
import CloseButton from "../../../components/TopRightCornerCloseButton/CloseButton";
import {
  Card,
  DateButton,
  DateButtonWrapper,
  DateSection,
  StoreWrapper,
  SubmitButton,
  SubmitButtonWrapper,
  TitleCss,
  WrapperCss,
  inputCss,
} from "./styled";
import { messages } from "../../../../locales/messages";
import { useContext, useState } from "react";
import { selectShortCutDateRangeFn } from "../../../../utils/SelectRangeDates";
import { DateRange } from "react-date-range";
import {
  CreateGfFlyerItemListDto,
  GfFlyerItemList,
  GfStore,
  UpdateGfFlyerItemListDto,
} from "@swagger/typescript-fetch-goflyer";
import { CustomAutocompleteMultiSelect } from "../../../components/CustomAutocomplete";
import { useStores } from "../../../../hooks/useStores";
import { makeStoreDisplayName } from "../../../../utils/dtoUtil/makeStoreDisplayName";
import { toastError } from "../../../../utils/pageUtil/toast";
import { GoFlyerAdminContext } from "../../../../contextApi/context";
import { adminApiService } from "../../../../utils/api";
import "./styles.css";
import { setValidationStartTimeTo1201AM } from "../../../../utils/dateUtil/setTimeTo1201AM";
import { setValidationEndTimeTo1159PM } from "../../../../utils/dateUtil/setTimeTo1159PM";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CustomModal from "../../../components/CustomModal";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { routes } from "../../../../utils/constant/navigationRoutes";

/**
 * CreateFlyerItemListModal Component
 *
 * This component represents a modal for creating or editing flyer items. It includes form elements
 * such as store selection, date range selection, and a submit button.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.onCloseModal - The function to close the modal.
 * @param {Function} props.onSaveFlyerItemList - The function to save the flyer item list.
 * @param {GfFlyerItemList} [props.updateFlyerItemList] - The flyer item list data for editing, if provided.
 * @example
 * <CreateFlyerItemListModal onCloseModal={handleClose} onSaveFlyerItemList={handleSave} />
 */
export const CreateFlyerItemListModal = ({
  onCloseModal,
  onSaveFlyerItemList,
  updateFlyerItemList,
  copy = false,
}: {
  onCloseModal: () => void;
  onSaveFlyerItemList: (flyerItemListId?: string) => void;
  updateFlyerItemList?: GfFlyerItemList;
  copy?: boolean;
}) => {
  const { t } = useTranslation();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();
  const navigate = useNavigate();
  const [dateButtonSelected, setDateButtonSelected] = useState(0);

  const [flyerItemList, setFlyerItemList] = useState<UpdateGfFlyerItemListDto>({
    ...updateFlyerItemList,
  });
  const { storesList, isStoreOptionLoading, handleOnStoreSearch } = useStores();

  /**
   * Handles button click event and updates date range for creating a flyer.
   * It's a ShortCut Button Function where we can select these 3 options
   * 1. fri-thur, 2. thur-wed and 3.fri-sun
   * @param button - The button identifier indicating the selected date shortcut.
   */
  const handleButtonClick = (button: number) => {
    const rangeDate = selectShortCutDateRangeFn(new Date(), button);
    setDateButtonSelected(button);
    if (rangeDate?.length) {
      setFlyerItemList({
        ...flyerItemList,
        validStartDate: rangeDate[0],
        validEndDate: rangeDate[1],
      });
    }
  };

  /**
   * Handles form submission for creating or updating a flyer item list.
   *
   * @async
   * @function
   * @name handleSubmit
   * @returns {void}
   */
  const handleSubmit = async () => {
    if (!flyerItemList?.stores || flyerItemList.stores.length === 0) {
      toastError(`${t(messages.Stores())} ${t(messages.is_Empty())}`);
      return;
    }

    if (!flyerItemList?.validStartDate) {
      toastError(`${t(messages.validStartDate())} ${t(messages.is_Empty())}`);
      return;
    }
    if (!flyerItemList?.validEndDate) {
      toastError(`${t(messages.validEndDate())} ${t(messages.is_Empty())}`);
      return;
    }

    const flyerItemListData = {
      ...flyerItemList,
      validStartDate: setValidationStartTimeTo1201AM(
        flyerItemList.validStartDate,
      ),
      validEndDate: setValidationEndTimeTo1159PM(flyerItemList.validEndDate),
      adminUser: state.gfAdminUser,
    };

    let result;
    if (flyerItemList?.id) {
      result =
        await adminApiService.updateOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
          { ...flyerItemListData, copy: copy },
          flyerItemList.id,
          authHeaders,
        );
      result && onSaveFlyerItemList();
    } else {
      result =
        await adminApiService.createOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
          flyerItemListData as CreateGfFlyerItemListDto,
        );

      result && navigate(routes.createFlyer.byId(result.id));
      result && navigate(routes.createFlyer.byId(result.id));
    }
  };
  return (
    <CustomModal
      onClose={onCloseModal}
      dataTestId="create-flyer-item-list-modal"
    >
      <Card data-testid="create-flyer-item-list-modal-card">
        <CloseButton
          onClose={onCloseModal}
          data-testid="create-flyer-item-list-modal-close-button"
        ></CloseButton>
        {updateFlyerItemList?.id && copy ? (
          <Typography
            variant="h5"
            color={"#ff8038"}
            marginLeft={1}
            position={"absolute"}
            top={11}
            right={50}
            fontWeight={"bold"}
            display={"flex"}
            alignItems={"center"}
            columnGap={1}
            data-testid="create-flyer-item-list-modal-copy-typography"
          >
            <FileCopyIcon data-testid="create-flyer-item-list-modal-file-copy-icon" />
            {t(messages.Copy())}
          </Typography>
        ) : (
          <></>
        )}
        <StoreWrapper data-testid="create-flyer-item-list-modal-store-wrapper">
          <CustomAutocompleteMultiSelect
            title={t(messages.Stores()) as string}
            optionsList={storesList?.map((store) => ({
              value: store,
              label: makeStoreDisplayName(store),
            }))}
            isLoading={isStoreOptionLoading}
            selectedValues={flyerItemList?.stores?.map((store) => ({
              value: store,
              label: makeStoreDisplayName(store),
            }))}
            multiSelect={false}
            handleOnChange={(stores: GfStore[] | undefined) => {
              stores &&
                setFlyerItemList({
                  ...flyerItemList,
                  stores: stores,
                });
            }}
            handleOnSearch={handleOnStoreSearch}
            WrapperCss={WrapperCss}
            inputCss={inputCss}
            titleCss={TitleCss}
            dataTestId={`store-select`}
          />
        </StoreWrapper>
        <DateSection data-testid="create-flyer-item-list-modal-date-section">
          <DateButtonWrapper data-testid="create-flyer-item-list-modal-date-button-wrapper">
            <DateButton
              isSelected={dateButtonSelected === 5}
              onClick={() => {
                handleButtonClick(5);
              }}
              data-testid="date-fri-thur-button"
            >
              {t(messages.Fri_Thur())}
            </DateButton>
            <DateButton
              isSelected={dateButtonSelected === 6}
              onClick={() => {
                handleButtonClick(6);
              }}
              data-testid="date-thur-wed-button"
            >
              {t(messages.Thur_Wed())}
            </DateButton>
            <DateButton
              isSelected={dateButtonSelected === 2}
              onClick={() => {
                handleButtonClick(2);
              }}
              data-testid="date-fri-sun-button"
            >
              {t(messages.Fri_Sun())}
            </DateButton>
          </DateButtonWrapper>
          <DateRange
            ranges={[
              {
                startDate:
                  flyerItemList && flyerItemList.validStartDate
                    ? new Date(flyerItemList.validStartDate)
                    : new Date(),
                endDate:
                  flyerItemList && flyerItemList?.validEndDate
                    ? new Date(flyerItemList.validEndDate)
                    : new Date(),
                key: "selection",
              },
            ]}
            months={2}
            moveRangeOnFirstSelection={false}
            showPreview={true}
            showDateDisplay={false}
            rangeColors={["#EF8200"]}
            displayMode="dateRange"
            direction="horizontal"
            color="#EF8200"
            onChange={(item) => {
              setFlyerItemList({
                ...flyerItemList,
                validStartDate: item.selection.startDate,
                validEndDate: item.selection.endDate,
              });
            }}
            data-testid="create-flyer-item-list-modal-date-range"
          />
        </DateSection>

        <SubmitButtonWrapper data-testid="create-flyer-item-list-modal-submit-button-wrapper">
          <SubmitButton onClick={handleSubmit} data-testid="submit-button">
            {flyerItemList?.id
              ? copy
                ? t(messages.Copy())
                : t(messages.Update())
              : `${t(messages.Submit())}`}
          </SubmitButton>
        </SubmitButtonWrapper>
      </Card>
    </CustomModal>
  );
};
